/**
 * One condition row — compact inline layout (dimension, match mode, values, remove).
 */

import type { FC } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';

import type { ConditionRowState, ConditionPredicate } from '../model';

import ConditionRowPredicateSelect from './ConditionRowPredicateSelect';
import ConditionRowValueSlot from './ConditionRowValueSlot';

export interface ConditionRowProps {
  row: ConditionRowState;
  onPredicateChange: (next: ConditionPredicate) => void;
  onValuesChange: (next: readonly string[]) => void;
  onTimeZoneChange?: (ianaId: string) => void;
  onRemove: () => void;
}

const ConditionRow: FC<ConditionRowProps> = ({
  row,
  onPredicateChange,
  onValuesChange,
  onTimeZoneChange,
  onRemove,
}) => (
  <li className="rounded-md border border-border/70 bg-card">
    <div className="flex gap-2 p-1.5 sm:items-start sm:gap-2 sm:p-2">
      <div className="flex shrink-0 flex-wrap items-center gap-x-1.5 gap-y-1">
        <span className="text-xs font-medium text-foreground">
          {row.descriptor.label}
        </span>
        <ConditionRowPredicateSelect
          predicate={row.predicate}
          onChange={onPredicateChange}
          ariaLabel={`${row.descriptor.label} predicate`}
          disabled={!row.descriptor.predicateSupported}
        />
      </div>
      <div className="min-w-0 flex-1">
        <ConditionRowValueSlot
          row={row}
          onChange={onValuesChange}
          onTimeZoneChange={onTimeZoneChange}
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label={`Remove ${row.descriptor.label} condition`}
        className="h-7 w-7 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive sm:mt-0.5"
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </div>
  </li>
);

export default ConditionRow;
